<template>
  <el-drawer
    v-model="visible"
    :close-on-click-modal="closeDrawerOnOutsideClick"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    destroy-on-close
    class="add-task-drawer"
    modal-class="add-task-drawer"
    @opened="onOpened"
    @closed="onClosed"
  >
    <template v-if="drawerOpened">
      <!-- ADD TASK -->
      <add-task-form
        :selected-project-id="projectId"
        :last-access-epoch-time="lastAccessEpochTime"
        :custom-sticky-top-pixels="-5"
        :for-kanban="forKanban"
        :for-listing="forListing"
        lives-in-drawer
        @task-created-for-kanban="(taskForKanban, createAnother, newEpochTime) => handleTaskCreatedForKanban(taskForKanban, createAnother, newEpochTime)"
        @task-created-for-listing="(createAnother, newEpochTime) => handleTaskCreatedForListing(createAnother, newEpochTime)"
        @title-desc-modified="closeDrawerOnOutsideClick = false"
        @title-desc-reset="closeDrawerOnOutsideClick = true"
        @canceled="handleCancel()"
      />
    </template>
  </el-drawer>
</template>

<script lang="ts">
import { ITaskForKanban } from '@/api/types/response.types'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'AddTaskDrawer',

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    // Default project to select after projects dropdown is loaded
    projectId: {
      type: Number,
      required: true,
    },
    /* Pass thru to add task form drawer for freshness info return on create */
    lastAccessEpochTime: {
      type: Number,
      default: null,
    },
    forKanban: {
      type: Boolean,
      default: false,
    },
    forListing: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'task-created-for-kanban',
    'task-created-for-listing',
    'update:showDrawer',
  ],

  data() {
    return {
      closeDrawerOnOutsideClick: true,
      drawerOpened: false,
      showAddTaskPanel: true,
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isTabletOrMobile(this.$mq)) {
        return '100%'
      }
      return '680px'
    },
  },

  methods: {
    onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
    },

    handleTaskCreatedForKanban(
      taskForKanban: ITaskForKanban,
      createAnother: boolean,
      newEpochTime: number,
    ) {
      if (!this.forKanban) {
        return
      }
      this.$emit('task-created-for-kanban', taskForKanban, newEpochTime)
      if (!createAnother) {
        this.handleCancel()
      }
    },

    handleTaskCreatedForListing(createAnother: boolean, newEpochTime: number) {
      this.$emit('task-created-for-listing', newEpochTime)
      if (!createAnother) {
        this.handleCancel()
      }
    },

    handleCancel() {
      this.$emit('update:showDrawer', false)
    },
  },
})
</script>

<style lang="scss">
.add-task-drawer {
  /* Default drawer overrides to remove title but keep "X" close icon */
  .el-drawer__header {
    padding-bottom: 0;
    border-bottom: none;
  }
  .el-drawer__body {
    padding-top: 0;
  }

  &__header {
    padding: 8px 0 8px 20px;

    > h1 {
      @include lettering--h3;
    }
  }

  &__nav {
    display: flex;
    padding-top: 5px;

    > div {
      @include lettering--p-sm;
      font-weight: $bold;
      padding: 6px 0;
      margin: 0 12px;
      display: inline-block;
      color: var(--color-text-third);
      cursor: pointer;

      &:first-child {
        margin-left: 0;
      }

      &.selected {
        color: var(--color-text-secondary);
        border-bottom: solid 2px $projects--theme-color;
      }
    }
  }
}
</style>
